import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import { useConsultations, useDeleteConsultation } from "@/hooks/api/useConsultations";
import { useTemplatesForFilter } from "@/hooks/api/useTemplates";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { ConsultationList } from "@/components/ConsultationList";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 10;

interface Filters {
  patientName: string;
  patientPersonalNumber: string;
  dateFrom: string;
  dateTo: string;
  templateId: string;
}

const EMPTY_FILTERS: Filters = {
  patientName: "",
  patientPersonalNumber: "",
  dateFrom: "",
  dateTo: "",
  templateId: "",
};

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canDelete = profile?.role === "admin" || profile?.role === "manager";
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const templateRef = useRef<HTMLDivElement>(null);

  const hasApplied = Object.values(applied).some(Boolean);

  const setDraftField = <K extends keyof Filters>(key: K, value: string) =>
    setDraft((f) => ({ ...f, [key]: value }));

  const applyFilters = useCallback(() => {
    setApplied(draft);
    setPage(1);
  }, [draft]);

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") applyFilters();
  };

  const { data: response, isLoading } = useConsultations({ page, filters: applied, limit: PAGE_SIZE });
  const { data: templatesResponse, isLoading: templatesLoading } =
    useTemplatesForFilter({ enabled: true });
  const deleteMutation = useDeleteConsultation();

  const templates = templatesResponse ?? [];
  const selectedTemplate = templates.find((tpl) => tpl.id === draft.templateId);
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((tpl) => tpl.name.toLowerCase().includes(q));
  }, [templates, templateSearch]);

  useEffect(() => {
    if (!templateOpen) return;
    const handler = (e: MouseEvent) => {
      if (templateRef.current && !templateRef.current.contains(e.target as Node)) {
        setTemplateOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [templateOpen]);

  const items = response?.data ?? [];
  const totalPages = Math.ceil((response?.total ?? 0) / PAGE_SIZE);

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-4">{t("dashboard.title")}</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <Button
          onClick={() => navigate("/consultation/new")}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("dashboard.newConsultation")}
        </Button>
        <Input
          className="h-8 text-sm w-full sm:w-44"
          placeholder={t("dashboard.filterPatientName")}
          value={draft.patientName}
          onChange={(e) => setDraftField("patientName", e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Input
          className="h-8 text-sm w-full sm:w-40"
          placeholder={t("dashboard.filterPersonalNumber")}
          value={draft.patientPersonalNumber}
          onChange={(e) =>
            setDraftField("patientPersonalNumber", e.target.value)
          }
          onKeyDown={handleKeyDown}
        />
        <input
          type="date"
          className="h-8 text-sm border rounded-md px-2 bg-background w-full sm:w-auto"
          value={draft.dateFrom}
          onChange={(e) => setDraftField("dateFrom", e.target.value)}
          title={t("dashboard.filterDateFrom")}
        />
        <input
          type="date"
          className="h-8 text-sm border rounded-md px-2 bg-background w-full sm:w-auto"
          value={draft.dateTo}
          onChange={(e) => setDraftField("dateTo", e.target.value)}
          title={t("dashboard.filterDateTo")}
        />
        <div ref={templateRef} className="relative w-full sm:w-56">
          <button
            type="button"
            onClick={() => setTemplateOpen((v) => !v)}
            className="h-8 w-full flex items-center justify-between gap-2 text-sm border rounded-md px-2 bg-background hover:bg-accent"
          >
            <span className={selectedTemplate ? "" : "text-muted-foreground"}>
              {selectedTemplate?.name ?? t("dashboard.allTemplates")}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
          {templateOpen && (
            <div className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-md">
              <div className="p-2 border-b">
                <Input
                  autoFocus
                  className="h-8 text-sm"
                  placeholder={t("common.search")}
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                  onClick={() => {
                    setDraftField("templateId", "");
                    setTemplateOpen(false);
                    setTemplateSearch("");
                  }}
                >
                  {t("dashboard.allTemplates")}
                </button>
                {templatesLoading && <Spinner className="py-3" />}
                {!templatesLoading &&
                  filteredTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-accent ${
                        tpl.id === draft.templateId ? "bg-accent" : ""
                      }`}
                      onClick={() => {
                        setDraftField("templateId", tpl.id);
                        setTemplateOpen(false);
                        setTemplateSearch("");
                      }}
                    >
                      {tpl.name}
                    </button>
                  ))}
                {!templatesLoading && filteredTemplates.length === 0 && (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    {t("common.noResults")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <Button size="sm" className="h-8" onClick={applyFilters}>
          <Search className="h-3.5 w-3.5 mr-1" />
          {t("common.search")}
        </Button>
        {hasApplied && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={clearFilters}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t("common.clear")}
          </Button>
        )}
      </div>

      <ConsultationList
        consultations={items}
        isLoading={isLoading}
        page={page}
        totalPages={totalPages}
        totalItems={response?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          hasApplied ? t("common.noResults") : t("dashboard.noConsultations")
        }
        onDelete={canDelete ? (id) => deleteMutation.mutate(id) : undefined}
        isDeleting={deleteMutation.isPending}
      />
    </Layout>
  );
}
